import React, { useState, useEffect } from 'react';
import { Button, Stack } from '@mui/material';
import api from 'utils/axios';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
    const [profile, setProfile] = useState({
        username: '',
        email: '',
        npi: '',
        tin: '',
        organization: '',
        role: '',
        email_verified: false
    });
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isResendingVerification, setIsResendingVerification] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                console.log("No token found, redirecting to login");
                navigate('/login');
                return;
            }

            const response = await api.get('/accounts/profile/');
            setProfile(response.data);
        } catch (error) {
            console.log("Error fetching profile:", error);
            setError('Failed to fetch profile');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        const errors = {};

        if (name === 'npi' && value) {
            // NPI should be 10 digits
            if (!/^\d{10}$/.test(value)) {
                errors.npi = 'NPI must be exactly 10 digits';
            }
        }

        if (name === 'tin' && value) {
            // TIN should be 9 digits (can be formatted as XX-XXXXXXX)
            const tinDigits = value.replace(/\D/g, '');
            if (tinDigits.length !== 9) {
                errors.tin = 'TIN must be exactly 9 digits';
            }
        }

        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Clear validation errors for this field
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
        });

        // Validate the field
        const fieldErrors = validateField(name, value);
        if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors(prev => ({
                ...prev,
                ...fieldErrors
            }));
        }

        setProfile(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate all fields before submission
        const allErrors = {};
        Object.keys(profile).forEach(key => {
            const fieldErrors = validateField(key, profile[key]);
            Object.assign(allErrors, fieldErrors);
        });

        if (Object.keys(allErrors).length > 0) {
            setValidationErrors(allErrors);
            setError('Please fix validation errors before submitting');
            return;
        }

        try {
            const { role, email_verified, ...editableProfile } = profile;
            const response = await api.patch('/accounts/profile/', editableProfile);
            setProfile(response.data);
            setSuccess('Profile updated successfully');
            setIsEditing(false);
            setValidationErrors({});
        } catch (error) {
            if (error.response?.data) {
                // Handle field-specific errors from the server
                const serverErrors = error.response.data;
                if (typeof serverErrors === 'object' && !serverErrors.error) {
                    setValidationErrors(serverErrors);
                } else {
                    setError(serverErrors.error || 'Failed to update profile');
                }
            } else {
                setError('Failed to update profile');
            }
        }
    };

    const handleResendVerification = async () => {
        setError('');
        setSuccess('');
        setIsResendingVerification(true);

        try {
            await api.post('/accounts/auth/verify/request/');
            setSuccess('Verification email sent! Please check your inbox.');
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to send verification email. Please contact support@meduriai.com for support.');
        } finally {
            setIsResendingVerification(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto p-6">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">Profile</h1>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {success}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <input
                        type="text"
                        name="username"
                        value={profile.username}
                        disabled
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="relative">
                        <input
                            type="email"
                            name="email"
                            value={profile.email}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm pr-10"
                        />
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                        {profile.email_verified ? (
                            <span className="text-green-600">✓ Email verified</span>
                        ) : (
                            <span className="text-red-600">✗ Email not verified</span>
                        )}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">NPI Number</label>
                    <input
                        type="text"
                        name="npi"
                        value={profile.npi || ''}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="National Provider Identifier"
                        className={`mt-1 block w-full rounded-md shadow-sm ${
                            validationErrors.npi
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                    />
                    {validationErrors.npi && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.npi}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                        10-digit National Provider Identifier for healthcare providers
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">TIN (Tax ID)</label>
                    <input
                        type="text"
                        name="tin"
                        value={profile.tin || ''}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Tax Identification Number"
                        className={`mt-1 block w-full rounded-md shadow-sm ${
                            validationErrors.tin
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                    />
                    {validationErrors.tin && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.tin}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                        Tax Identification Number - required for automatic claims data retrieval upon login
                    </p>
                    {profile.role === 'provider' && !profile.tin && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-800">
                                💡 <strong>Tip:</strong> Adding your TIN enables automatic claims data retrieval when you log in,
                                keeping your claims information up-to-date.
                            </p>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Organization</label>
                    <input
                        type="text"
                        name="organization"
                        value={profile.organization || ''}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <input
                        type="text"
                        name="role"
                        value={profile.role}
                        disabled
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100"
                    />
                </div>

                {!profile.email_verified && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex items-center">
                            <div>
                                <h3 className="text-sm font-medium text-yellow-800">
                                    Email verification required
                                </h3>
                                <p className="text-sm text-yellow-700 mt-1">
                                    Please verify your email address to access all features.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={isResendingVerification}
                                    className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isResendingVerification ? 'Sending...' : 'Resend verification email'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    {isEditing ? (
                        <>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => {
                                    setIsEditing(false);
                                    setValidationErrors({});
                                    setError('');
                                    setSuccess('');
                                    fetchProfile();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained">
                                Save Changes
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            variant="contained"
                            onClick={() => setIsEditing(true)}
                        >
                            Edit Profile
                        </Button>
                    )}
                </Stack>
            </form>
        </div>
    );
};

export default Profile;
